"use client";

import { Html } from "@/Components";
import { CenterOptions } from "@/Helpers/CenterOptions";
import { useT } from "@/Hooks";
import { BlogsSectionProps } from "@/Templates/SectionsProps";
import { FC, ReactElement, ReactNode } from "react";
import HomeEightBlogSectionGenerated, {
  HomeEightBlogSectionGeneratedProps,
} from "../Sections/_generated/HomeEightBlogSection.generated";
import HomeEightBlogSliderEightGenerated from "../Sections/_generated/HomeEightBlogSliderEight.generated";
import HomeEighteenBlogSectionGenerated, {
  HomeEighteenBlogSectionGeneratedProps,
} from "../Sections/_generated/HomeEighteenBlogSection.generated";
import HomeElevenBlogsSectionGenerated, {
  HomeElevenBlogsSectionGeneratedProps,
} from "../Sections/_generated/HomeElevenBlogsSection.generated";
import HomeFifteenBlogSectionGenerated, {
  HomeFifteenBlogSectionGeneratedProps,
} from "../Sections/_generated/HomeFifteenBlogSection.generated";
import HomeFiveStartNewsSectionGenerated, {
  HomeFiveStartNewsSectionGeneratedProps,
} from "../Sections/_generated/HomeFiveStartNewsSection.generated";
import HomeFourBlogSectionGenerated, {
  HomeFourBlogSectionGeneratedProps,
} from "../Sections/_generated/HomeFourBlogSection.generated";
import HomeFourteenBlogsSectionGenerated, {
  HomeFourteenBlogsSectionGeneratedProps,
} from "../Sections/_generated/HomeFourteenBlogsSection.generated";
import HomeNineArticleSectionGenerated, {
  HomeNineArticleSectionGeneratedProps,
} from "../Sections/_generated/HomeNineArticleSection.generated";
import HomeOneArticleSectionGenerated, {
  HomeOneArticleSectionGeneratedProps,
} from "../Sections/_generated/HomeOneArticleSection.generated";
import HomeSevenBlogSectionGenerated, {
  HomeSevenBlogSectionGeneratedProps,
} from "../Sections/_generated/HomeSevenBlogSection.generated";
import HomeSeventeenBlogSectionGenerated, {
  HomeSeventeenBlogSectionGeneratedProps,
} from "../Sections/_generated/HomeSeventeenBlogSection.generated";
import HomeSixTestimonialSectionTwoGenerated, {
  HomeSixTestimonialSectionTwoGeneratedProps,
} from "../Sections/_generated/HomeSixTestimonialSectionTwo.generated";
import HomeSixteenBlogSectionGenerated, {
  HomeSixteenBlogSectionGeneratedProps,
} from "../Sections/_generated/HomeSixteenBlogSection.generated";
import HomeTenBlogSectionGenerated, {
  HomeTenBlogSectionGeneratedProps,
} from "../Sections/_generated/HomeTenBlogSection.generated";
import HomeThirteenBlogSectionGenerated, {
  HomeThirteenBlogSectionGeneratedProps,
} from "../Sections/_generated/HomeThirteenBlogSection.generated";
import HomeThreeBlogSectionGenerated, {
  HomeThreeBlogSectionGeneratedProps,
} from "../Sections/_generated/HomeThreeBlogSection.generated";
import HomeTwelveBlogSectionGenerated, {
  HomeTwelveBlogSectionGeneratedProps,
} from "../Sections/_generated/HomeTwelveBlogSection.generated";
import HomeTwoBlogSectionGenerated, {
  HomeTwoBlogSectionGeneratedProps,
} from "../Sections/_generated/HomeTwoBlogSection.generated";
import {
  buildGeneratedSectionProps,
  renderGeneratedSection,
} from "./generated-section-renderer";

type BlogsTranslate = ReturnType<typeof useT>;
type GeneratedBlogsRenderer = (
  props: BlogsSectionProps,
  t: BlogsTranslate,
) => ReactElement;
type GeneratedBlogsProps = Record<string, unknown>;
type GeneratedBlogsItem = Record<string, unknown>;
type BlogDataItem = BlogsSectionProps["dataSource"]["Items"][number];

const BlogsAppearance =
  CenterOptions.SiteTemplate.SiteTemplateSettings.SiteTemplateBlogsAppearance;

const titleKeyMatchers = [/H20H2Text$/];
const titleDecorationKeyMatchers = [/H20Span\d+SpanText$/, /H20H2Text2$/];
const subtitleKeyMatchers = [/DivText$/];
const descriptionKeyMatchers = [/P0PText$/];
const sectionViewAllHrefMatchers = [/Div2Link0LinkHref$/, /Div0Link0LinkHref$/];
const sectionViewAllVisibleMatchers = [
  /Div2Link0LinkVisible$/,
  /Div0Link0LinkVisible$/,
];

function buildGeneratedBlogsProps<T extends GeneratedBlogsProps>(
  sectionKey: string,
  overrides?: Partial<T>,
): T {
  return buildGeneratedSectionProps<T>(sectionKey, overrides);
}

function findFirstMatchingKey(
  target: GeneratedBlogsProps | GeneratedBlogsItem,
  matchers: RegExp[],
): string | undefined {
  return Object.keys(target).find((key) =>
    matchers.some((matcher) => matcher.test(key)),
  );
}

function setFirstMatchingValue(
  target: GeneratedBlogsProps | GeneratedBlogsItem,
  matchers: RegExp[],
  value: unknown,
): void {
  if (value === null || value === undefined || value === "") {
    return;
  }

  const key = findFirstMatchingKey(target, matchers);
  if (!key) {
    return;
  }

  target[key] = value;
}

function setAllMatchingValues(
  target: GeneratedBlogsProps | GeneratedBlogsItem,
  matchers: RegExp[],
  value: unknown,
): void {
  for (const key of Object.keys(target)) {
    if (matchers.some((matcher) => matcher.test(key))) {
      target[key] = value;
    }
  }
}

function getBlogImageUrl(item: BlogDataItem): string | undefined {
  const url = item.Image?.Url;
  return typeof url === "string" && url.length > 0 ? url : undefined;
}

function parseBlogDate(value: string | undefined) {
  if (!value) {
    return undefined;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }

  return date;
}

function formatBlogDateParts(value: string | undefined) {
  const date = parseBlogDate(value);
  if (!date) {
    return undefined;
  }

  const day = new Intl.DateTimeFormat("en-US", { day: "2-digit" }).format(date);
  const month = new Intl.DateTimeFormat("en-US", { month: "short" }).format(
    date,
  );
  const year = new Intl.DateTimeFormat("en-US", { year: "numeric" }).format(
    date,
  );

  return {
    day,
    month,
    full: `${day}, ${month} ${year}`,
    compact: `${day} ${month} ${year}`,
  };
}

function formatPersianBlogDateParts(value: string | undefined) {
  const date = parseBlogDate(value);
  if (!date) {
    return undefined;
  }

  const day = new Intl.DateTimeFormat("fa-IR-u-ca-persian", {
    day: "numeric",
  }).format(date);
  const month = new Intl.DateTimeFormat("fa-IR-u-ca-persian", {
    month: "short",
  }).format(date);
  const year = new Intl.DateTimeFormat("fa-IR-u-ca-persian", {
    year: "numeric",
  }).format(date);

  return {
    day,
    month,
    year,
    full: `${day}، ${month} ${year}`,
    compact: `${day} ${month} ${year}`,
  };
}

function withFallback<T>(value: T | undefined, fallback: T): T {
  return value ?? fallback;
}

function applyBlogLinks(
  nextItem: GeneratedBlogsItem,
  blog: BlogDataItem,
): void {
  const blogLink = blog.Link || "#";
  const categoryLink = blog.CategoryLink || blogLink;

  if ("href" in nextItem) {
    nextItem.href = blogLink;
  }
  if ("link0Href" in nextItem) {
    nextItem.link0Href = blogLink;
  }
  if ("link0Href2" in nextItem) {
    nextItem.link0Href2 = blogLink;
  }
  if ("link0Href3" in nextItem) {
    nextItem.link0Href3 = blogLink;
  }
  if ("link1Href" in nextItem) {
    nextItem.link1Href = categoryLink;
  }
  if ("a0Href" in nextItem) {
    nextItem.a0Href = blogLink;
  }
  if ("a0Href2" in nextItem) {
    nextItem.a0Href2 = blogLink;
  }
  if ("a0Href3" in nextItem) {
    nextItem.a0Href3 = blogLink;
  }
}

function applyBlogImage(
  nextItem: GeneratedBlogsItem,
  blog: BlogDataItem,
): void {
  const imageUrl = getBlogImageUrl(blog);
  if (!imageUrl) {
    return;
  }

  if ("src" in nextItem) {
    nextItem.src = imageUrl;
  }

  if ("imageUrl" in nextItem) {
    nextItem.imageUrl = imageUrl;
  }

  if (!("src" in nextItem) && "imageWithBasePath0Src" in nextItem) {
    nextItem.imageWithBasePath0Src = imageUrl;
  }

  if ("alt" in nextItem) {
    nextItem.alt = blog.Title;
  }

  if (
    "imageWithBasePath0Alt" in nextItem &&
    !("src" in nextItem) &&
    "imageWithBasePath0Src" in nextItem
  ) {
    nextItem.imageWithBasePath0Alt = blog.Title;
  }
}

function applyBlogDate(nextItem: GeneratedBlogsItem, blog: BlogDataItem): void {
  const dateParts = formatBlogDateParts(blog.DateRegister);
  const persianDateParts = formatPersianBlogDateParts(blog.DateRegister);
  if (!dateParts || !persianDateParts) {
    return;
  }

  if ("div0Text" in nextItem) {
    nextItem.div0Text = persianDateParts.day;
    if ("span0Text" in nextItem) {
      nextItem.span0Text = persianDateParts.month;
    }
  }
  if ("span2Text" in nextItem) {
    nextItem.span2Text = persianDateParts.compact;
  }
  if ("div1Text" in nextItem) {
    nextItem.div1Text = persianDateParts.full;
  }
  if ("p0Text" in nextItem && !("div0Text" in nextItem)) {
    nextItem.p0Text = persianDateParts.full;
  }
}

function applyBlogContent(
  baseItem: GeneratedBlogsItem,
  blog: BlogDataItem,
  siteAdminAuthorText: ReactNode,
  readMoreText: ReactNode,
): GeneratedBlogsItem {
  const nextItem: GeneratedBlogsItem = {
    ...baseItem,
    key: blog.Id,
  };
  const descriptionNode = blog.ContentShort ? (
    <Html value={blog.ContentShort} />
  ) : undefined;
  const dateParts = formatBlogDateParts(blog.DateRegister);
  const persianDateParts = formatPersianBlogDateParts(blog.DateRegister);

  applyBlogLinks(nextItem, blog);
  applyBlogImage(nextItem, blog);
  applyBlogDate(nextItem, blog);

  if ("imageUrl" in nextItem) {
    nextItem.badge = blog.CategoryName;
    nextItem.title = blog.Title;
    nextItem.description = blog.ContentShort ?? "";
    nextItem.date =
      persianDateParts?.full ?? dateParts?.full ?? blog.DateRegister ?? "";
    return nextItem;
  }

  if (
    "span0Span0Text" in nextItem &&
    "link0Link0Text" in nextItem &&
    "imageWithBasePath0Src" in nextItem
  ) {
    nextItem.span0Span0Text = blog.CategoryName;
    nextItem.link0Text = siteAdminAuthorText;
    nextItem.link0Link0Text = blog.Title;
    return nextItem;
  }

  if ("span0Span0Text" in nextItem) {
    nextItem.link0Text = blog.Title;
    nextItem.span0Span0Text = blog.CategoryName;
    if ("link0Link0Text" in nextItem) {
      nextItem.link0Link0Text = readMoreText;
    }
    if ("p0Text" in nextItem) {
      nextItem.p0Text = descriptionNode;
    }
    return nextItem;
  }

  if ("a0A0Text" in nextItem) {
    nextItem.a0Text = blog.CategoryName;
    nextItem.a0A0Text = blog.Title;
    if ("a0A0Text3" in nextItem) {
      nextItem.a0A0Text3 = readMoreText;
    }
    if ("a0A0Text2" in nextItem) {
      nextItem.a0A0Text2 = siteAdminAuthorText;
    }
    if ("a1Text" in nextItem) {
      nextItem.a1Text = siteAdminAuthorText;
    }

    if ("p0P0Text" in nextItem) {
      nextItem.p0P0Text = descriptionNode;
      nextItem.p0Text = withFallback(
        persianDateParts?.full,
        blog.DateRegister ?? "",
      );
    } else if ("div1Text" in nextItem) {
      nextItem.p0Text = descriptionNode;
      nextItem.div1Text = withFallback(
        persianDateParts?.full,
        blog.DateRegister ?? "",
      );
    } else if ("p0Text" in nextItem) {
      nextItem.p0Text = descriptionNode;
    }

    return nextItem;
  }

  if ("link1Text" in nextItem) {
    nextItem.link1Text = blog.CategoryName;
    nextItem.link0Text = blog.Title;
    if ("link1Link1Text" in nextItem) {
      nextItem.link1Link1Text = siteAdminAuthorText;
    }
    if ("span0Text" in nextItem) {
      nextItem.span0Text = siteAdminAuthorText;
    }

    if ("p0P0Text" in nextItem) {
      nextItem.p0P0Text = descriptionNode;
      nextItem.p0Text = withFallback(
        persianDateParts?.full,
        blog.DateRegister ?? "",
      );
    } else if ("p0Text" in nextItem) {
      nextItem.p0Text = descriptionNode;
    }

    return nextItem;
  }

  if ("link0Link0Text" in nextItem) {
    nextItem.link0Link0Text = blog.Title;
    if (!("div0Text" in nextItem)) {
      nextItem.link0Link0Text = readMoreText;
    }

    if ("span0Span0Text" in nextItem) {
      nextItem.span0Span0Text = blog.CategoryName;
      if ("link0Text" in nextItem) {
        nextItem.link0Text = siteAdminAuthorText;
      }
    } else if ("link0Text" in nextItem) {
      nextItem.link0Text =
        "imageWithBasePath0Src" in nextItem && "div0Text" in nextItem
          ? siteAdminAuthorText
          : blog.CategoryName;
    }

    if ("p0Text" in nextItem && !("div0Text" in nextItem)) {
      nextItem.p0Text = withFallback(
        persianDateParts?.full,
        blog.DateRegister ?? "",
      );
    }

    return nextItem;
  }

  if ("span2Text" in nextItem) {
    nextItem.link0Text = blog.Title;
    nextItem.span0Text = blog.CategoryName;
    nextItem.span2Text = withFallback(
      persianDateParts?.compact,
      blog.DateRegister ?? "",
    );
    return nextItem;
  }

  if ("link0Text" in nextItem) {
    nextItem.link0Text = blog.Title;

    if ("p0P0Text" in nextItem) {
      nextItem.p0P0Text = blog.CategoryName;
    }

    if ("span0Text" in nextItem && !("div0Text" in nextItem)) {
      nextItem.span0Text =
        "imageWithBasePath0Src" in nextItem
          ? siteAdminAuthorText
          : blog.CategoryName;
    }

    if ("p0Text" in nextItem) {
      nextItem.p0Text =
        "span0Text" in nextItem && !("div0Text" in nextItem)
          ? withFallback(persianDateParts?.full, blog.DateRegister ?? "")
          : descriptionNode;
    }
  }

  if ("link0Link0Text" in nextItem && !("div0Text" in nextItem)) {
    nextItem.link0Link0Text = readMoreText;
  }

  if ("a0A0Text3" in nextItem) {
    nextItem.a0A0Text3 = readMoreText;
  }

  return nextItem;
}

function isBlogCollectionItem(item: GeneratedBlogsItem): boolean {
  return (
    "src" in item ||
    "imageUrl" in item ||
    "link0Text" in item ||
    "link0Link0Text" in item ||
    "a0A0Text" in item ||
    "title" in item
  );
}

function applyBlogCollection(
  defaults: GeneratedBlogsItem[],
  items: BlogDataItem[],
  siteAdminAuthorText: ReactNode,
  readMoreText: ReactNode,
): GeneratedBlogsItem[] {
  if (items.length === 0 || defaults.length === 0) {
    return [];
  }

  return items.map((blog, index) =>
    applyBlogContent(
      defaults[index % defaults.length] ?? {},
      blog,
      siteAdminAuthorText,
      readMoreText,
    ),
  );
}

function applyHomeFifteenCards(
  generatedProps: HomeFifteenBlogSectionGeneratedProps,
  items: BlogDataItem[],
  siteAdminAuthorText: ReactNode,
  readMoreText: ReactNode,
): HomeFifteenBlogSectionGeneratedProps {
  const nextProps: GeneratedBlogsProps = { ...generatedProps };
  const cards = items.slice(0, 3);

  const applyCard = (
    item: BlogDataItem | undefined,
    prefix:
      | "sectionDiv0Div1Div0Div0Div1"
      | "sectionDiv0Div1Div1Div0Div0"
      | "sectionDiv0Div1Div2Div0Div1",
    imagePrefix:
      | "sectionDiv0Div1Div0Div0Div0ImageWithBasePath0Image"
      | "sectionDiv0Div1Div1Div0Div1ImageWithBasePath0Image"
      | "sectionDiv0Div1Div2Div0Div0ImageWithBasePath0Image",
    infoKey: "blogSectionBlogs" | "blogSectionBlogs2" | "blogSectionBlogs3",
  ) => {
    if (!item) {
      return;
    }

    const imageUrl = getBlogImageUrl(item);
    const dateParts = formatPersianBlogDateParts(item.DateRegister);

    nextProps[`${prefix}Link0LinkText`] = item.CategoryName;
    nextProps[`${prefix}Link0LinkHref`] = item.CategoryLink || item.Link || "#";
    nextProps[`${prefix}H30Link0LinkText`] = item.Title;
    nextProps[`${prefix}H30Link0LinkHref`] = item.Link || "#";
    nextProps[`${prefix}Link1LinkHref`] = item.Link || "#";
    nextProps[`${prefix}Link1LinkText`] = readMoreText;

    if (imageUrl) {
      nextProps[`${imagePrefix}Src`] = imageUrl;
    }

    const defaults =
      (nextProps[infoKey] as GeneratedBlogsItem[] | undefined) ?? [];
    nextProps[infoKey] = defaults.map((infoItem, index) => ({
      ...infoItem,
      key: `${item.Id}-${index}`,
      pText:
        index === 0
          ? withFallback(dateParts?.full, item.DateRegister ?? "")
          : siteAdminAuthorText,
    }));
  };

  applyCard(
    cards[0],
    "sectionDiv0Div1Div0Div0Div1",
    "sectionDiv0Div1Div0Div0Div0ImageWithBasePath0Image",
    "blogSectionBlogs",
  );
  applyCard(
    cards[1],
    "sectionDiv0Div1Div1Div0Div0",
    "sectionDiv0Div1Div1Div0Div1ImageWithBasePath0Image",
    "blogSectionBlogs2",
  );
  applyCard(
    cards[2],
    "sectionDiv0Div1Div2Div0Div1",
    "sectionDiv0Div1Div2Div0Div0ImageWithBasePath0Image",
    "blogSectionBlogs3",
  );

  return nextProps as HomeFifteenBlogSectionGeneratedProps;
}

function buildHomeEightSliderBlogs(items: BlogDataItem[]) {
  return items.map((item) => ({
    imageUrl: item.Image?.Url ?? "",
    badge: item.CategoryName ?? "",
    title: item.Title ?? "",
    description: item.ContentShort ?? "",
    date: withFallback(
      formatPersianBlogDateParts(item.DateRegister)?.full,
      item.DateRegister ?? "",
    ),
    alt: item.Title ?? "blog-img",
  }));
}

function applyBlogsOverrides<T extends GeneratedBlogsProps>(
  generatedProps: T,
  props: BlogsSectionProps,
  t: BlogsTranslate,
): T {
  const nextProps: GeneratedBlogsProps = {
    ...generatedProps,
  };
  const descriptionNode = props.setting.Description ? (
    <Html value={props.setting.Description} />
  ) : undefined;
  const viewAllText = t("landing.blogs.viewAll", undefined, "View all blogs");
  const readMoreText = t("landing.blogs.readMore", undefined, "Read More");
  const siteAdminAuthorText = t(
    "landing.blogs.siteAdmin",
    undefined,
    "Site Admin",
  );

  setFirstMatchingValue(nextProps, titleKeyMatchers, props.setting.Title);
  if (props.setting.Title) {
    setAllMatchingValues(nextProps, titleDecorationKeyMatchers, "");
  }

  setFirstMatchingValue(nextProps, subtitleKeyMatchers, descriptionNode);
  setFirstMatchingValue(nextProps, descriptionKeyMatchers, descriptionNode);
  setFirstMatchingValue(nextProps, sectionViewAllHrefMatchers, "/Blogs");
  setFirstMatchingValue(
    nextProps,
    [/Div2Link0LinkText$/, /Div0Link0LinkText$/],
    viewAllText,
  );
  setFirstMatchingValue(
    nextProps,
    sectionViewAllVisibleMatchers,
    props.setting.ViewAllButtonDisplayStatus.Key ===
      CenterOptions.Enum.Status.Active.key,
  );

  const collectionKeys = Object.keys(nextProps).filter((key) => {
    const value = nextProps[key];
    return (
      Array.isArray(value) &&
      value.length > 0 &&
      typeof value[0] === "object" &&
      value[0] !== null &&
      isBlogCollectionItem(value[0] as GeneratedBlogsItem)
    );
  });

  collectionKeys.forEach((key) => {
    const defaults = nextProps[key] as GeneratedBlogsItem[];
    nextProps[key] = applyBlogCollection(
      defaults,
      props.dataSource.Items,
      siteAdminAuthorText,
      readMoreText,
    );
  });

  return nextProps as T;
}

function renderPlainTitle(
  title: string | undefined,
  className: string,
  defaultNode: ReactNode,
): ReactNode {
  if (!title) {
    return defaultNode;
  }

  return className ? <h2 className={className}>{title}</h2> : <h2>{title}</h2>;
}

const generatedBlogsRenderers: Record<string, GeneratedBlogsRenderer> = {
  [BlogsAppearance.Home1.key]: (props, t) => (
    <HomeOneArticleSectionGenerated
      {...applyBlogsOverrides(
        buildGeneratedBlogsProps<HomeOneArticleSectionGeneratedProps>(
          "HomeOneArticleSection",
        ),
        props,
        t,
      )}
      sectionDiv0Div0H20H2Renderer={(defaultNode) =>
        renderPlainTitle(props.setting.Title, "section-title", defaultNode)
      }
    />
  ),
  [BlogsAppearance.Home2.key]: (props, t) => (
    <HomeTwoBlogSectionGenerated
      {...applyBlogsOverrides(
        buildGeneratedBlogsProps<HomeTwoBlogSectionGeneratedProps>(
          "HomeTwoBlogSection",
        ),
        props,
        t,
      )}
    />
  ),
  [BlogsAppearance.Home3.key]: (props, t) => (
    <HomeThreeBlogSectionGenerated
      {...applyBlogsOverrides(
        buildGeneratedBlogsProps<HomeThreeBlogSectionGeneratedProps>(
          "HomeThreeBlogSection",
        ),
        props,
        t,
      )}
      sectionDiv0Div0H20H2Renderer={(defaultNode) =>
        renderPlainTitle(props.setting.Title, "section-title", defaultNode)
      }
    />
  ),
  [BlogsAppearance.Home4.key]: (props, t) => (
    <HomeFourBlogSectionGenerated
      {...applyBlogsOverrides(
        buildGeneratedBlogsProps<HomeFourBlogSectionGeneratedProps>(
          "HomeFourBlogSection",
        ),
        props,
        t,
      )}
      sectionDiv0Div0Div0Div0Div0H20H2Renderer={(defaultNode) =>
        renderPlainTitle(props.setting.Title, "section-title", defaultNode)
      }
    />
  ),
  [BlogsAppearance.Home5.key]: (props, t) => (
    <HomeFiveStartNewsSectionGenerated
      {...applyBlogsOverrides(
        buildGeneratedBlogsProps<HomeFiveStartNewsSectionGeneratedProps>(
          "HomeFiveStartNewsSection",
        ),
        props,
        t,
      )}
    />
  ),
  [BlogsAppearance.Home6.key]: (props, t) => (
    <HomeSixTestimonialSectionTwoGenerated
      {...applyBlogsOverrides(
        buildGeneratedBlogsProps<HomeSixTestimonialSectionTwoGeneratedProps>(
          "HomeSixTestimonialSectionTwo",
        ),
        props,
        t,
      )}
    />
  ),
  [BlogsAppearance.Home7.key]: (props, t) => (
    <HomeSevenBlogSectionGenerated
      {...applyBlogsOverrides(
        buildGeneratedBlogsProps<HomeSevenBlogSectionGeneratedProps>(
          "HomeSevenBlogSection",
        ),
        props,
        t,
      )}
    />
  ),
  [BlogsAppearance.Home8.key]: (props, t) => (
    <>
      <HomeEightBlogSectionGenerated
        {...applyBlogsOverrides(
          buildGeneratedBlogsProps<HomeEightBlogSectionGeneratedProps>(
            "HomeEightBlogSection",
          ),
          props,
          t,
        )}
        sectionDiv0H20H2Renderer={(defaultNode) =>
          renderPlainTitle(props.setting.Title, "section-title", defaultNode)
        }
      />
      <HomeEightBlogSliderEightGenerated
        blogs={buildHomeEightSliderBlogs(props.dataSource.Items)}
      />
    </>
  ),
  [BlogsAppearance.Home9.key]: (props, t) => (
    <HomeNineArticleSectionGenerated
      {...applyBlogsOverrides(
        buildGeneratedBlogsProps<HomeNineArticleSectionGeneratedProps>(
          "HomeNineArticleSection",
        ),
        props,
        t,
      )}
    />
  ),
  [BlogsAppearance.Home10.key]: (props, t) => (
    <HomeTenBlogSectionGenerated
      {...applyBlogsOverrides(
        buildGeneratedBlogsProps<HomeTenBlogSectionGeneratedProps>(
          "HomeTenBlogSection",
        ),
        props,
        t,
      )}
    />
  ),
  [BlogsAppearance.Home11.key]: (props, t) => (
    <HomeElevenBlogsSectionGenerated
      {...applyBlogsOverrides(
        buildGeneratedBlogsProps<HomeElevenBlogsSectionGeneratedProps>(
          "HomeElevenBlogsSection",
        ),
        props,
        t,
      )}
      sectionDiv0Div1ClassName="row g-3 g-md-5"
    />
  ),
  [BlogsAppearance.Home12.key]: (props, t) => (
    <HomeTwelveBlogSectionGenerated
      {...applyBlogsOverrides(
        buildGeneratedBlogsProps<HomeTwelveBlogSectionGeneratedProps>(
          "HomeTwelveBlogSection",
        ),
        props,
        t,
      )}
      sectionDiv0Div0H20H2Renderer={(defaultNode) =>
        renderPlainTitle(props.setting.Title, "section-title", defaultNode)
      }
    />
  ),
  [BlogsAppearance.Home13.key]: (props, t) => (
    <HomeThirteenBlogSectionGenerated
      {...applyBlogsOverrides(
        buildGeneratedBlogsProps<HomeThirteenBlogSectionGeneratedProps>(
          "HomeThirteenBlogSection",
        ),
        props,
        t,
      )}
    />
  ),
  [BlogsAppearance.Home14.key]: (props, t) => (
    <HomeFourteenBlogsSectionGenerated
      {...applyBlogsOverrides(
        buildGeneratedBlogsProps<HomeFourteenBlogsSectionGeneratedProps>(
          "HomeFourteenBlogsSection",
        ),
        props,
        t,
      )}
    />
  ),
  [BlogsAppearance.Home15.key]: (props, t) => (
    <HomeFifteenBlogSectionGenerated
      {...applyHomeFifteenCards(
        applyBlogsOverrides(
          buildGeneratedBlogsProps<HomeFifteenBlogSectionGeneratedProps>(
            "HomeFifteenBlogSection",
          ),
          props,
          t,
        ),
        props.dataSource.Items,
        t("landing.blogs.siteAdmin", undefined, "Site Admin"),
        t("landing.blogs.readMore", undefined, "Read More"),
      )}
    />
  ),
  [BlogsAppearance.Home16.key]: (props, t) => (
    <HomeSixteenBlogSectionGenerated
      {...applyBlogsOverrides(
        buildGeneratedBlogsProps<HomeSixteenBlogSectionGeneratedProps>(
          "HomeSixteenBlogSection",
        ),
        props,
        t,
      )}
    />
  ),
  [BlogsAppearance.Home17.key]: (props, t) => (
    <HomeSeventeenBlogSectionGenerated
      {...applyBlogsOverrides(
        buildGeneratedBlogsProps<HomeSeventeenBlogSectionGeneratedProps>(
          "HomeSeventeenBlogSection",
        ),
        props,
        t,
      )}
    />
  ),
  [BlogsAppearance.Home18.key]: (props, t) => (
    <HomeEighteenBlogSectionGenerated
      {...applyBlogsOverrides(
        buildGeneratedBlogsProps<HomeEighteenBlogSectionGeneratedProps>(
          "HomeEighteenBlogSection",
        ),
        props,
        t,
      )}
      sectionDiv0Div0Div0DivText={<Html value={props.setting.Description} />}
      sectionDiv0Div0H20H2Renderer={(defaultNode) =>
        renderPlainTitle(props.setting.Title, "section-title", defaultNode)
      }
    />
  ),
};

const GeneratedBlogsSection: FC<BlogsSectionProps> = (props) => {
  const t = useT();

  return renderGeneratedSection(
    props,
    Object.fromEntries(
      Object.entries(generatedBlogsRenderers).map(
        ([appearanceKey, renderer]) => [
          appearanceKey,
          (sectionProps: BlogsSectionProps) => renderer(sectionProps, t),
        ],
      ),
    ),
    BlogsAppearance.Home1.key,
    (sectionProps) => sectionProps.setting.Appearance?.Key,
  );
};

export default GeneratedBlogsSection;
